import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { isValidWebAppLocale } from '~/utils/guards/isValidWebAppLocale';

export type ContentfulPageRouteMode = 'slug' | 'id';

/**
 * Detects routes that load a Contentful marketing/CMS page — the same surface as
 * `pages/[locale]/[...slug].vue` and `pages/[locale]/id/[id].vue`.
 *
 * Used to skip `/api/contentful` on shop, payment return, or technical pages so
 * layout/analytics can share the page `useFetch` without firing bogus requests.
 */
export function resolveContentfulPageRouteMode(
  route: Pick<RouteLocationNormalizedLoaded, 'path'>
): false | ContentfulPageRouteMode {
  const segments = route.path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return false;
  }

  const localeCandidate = segments[0]?.toLowerCase() ?? '';
  if (!isValidWebAppLocale(localeCandidate)) {
    return false;
  }

  const rest = segments.slice(1);
  const head = rest[0]?.toLowerCase();

  // Homepage: /{locale} only — handled by the catch-all slug page with empty slug.
  if (!head) {
    return 'slug';
  }

  if (head === 'shop') {
    return false;
  }
  if (head === 'payment' && rest[1]?.toLowerCase() === 'return') {
    return false;
  }
  if (head === '_technical') {
    return false;
  }
  if (head === 'id' && rest[1]) {
    return 'id';
  }

  return 'slug';
}
